import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { ProvenanceMap } from '../components/ui/ProvenanceIndicator';

// ── Loose RPC helper (get_listing_links is dynamic) ─────────────────────────

type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Exported types ──────────────────────────────────────────────────────────

/** Wide product row shape returned by useProductDetail. Index signature
 * allows dynamic column lookup (used by resolveListingUrl). */
export interface ProductDetailRow {
  id: string;
  isg_sku: string | null;
  title: string | null;
  brand: string | null;
  category_algopix?: string | null;
  upc?: string | null;
  asin?: string | null;
  ean?: string | null;
  mpn?: string | null;
  model?: string | null;
  supplier_part_no?: string | null;
  // U10 A1 — junction-assembled at the fetch (primary product_distributors row →
  // cost_usd ?? null); products.cost_usd is the read-copy that DROPS at slice B.
  cost_usd?: number | null;
  // U10 A1 — the junction embed (primary row only via the embedded filter);
  // canonical cost ground once products.cost_usd drops.
  product_distributors?: { cost_usd: number | null; is_primary: boolean }[] | null;
  msrp?: number | null;
  image_url?: string | null;
  // W4c — the resolved product's Amazon listing image (facts-worker captured); ProductDetail
  // renders it first in the hero chain (amazon_image_url || image_url || algopix_data.image_url).
  amazon_image_url?: string | null;
  weight_lbs?: number | null;
  length_in?: number | null;
  width_in?: number | null;
  height_in?: number | null;
  color?: string | null;
  amazon_buy_box_price?: number | null;
  amazon_demand_rating?: string | null;
  amazon_monthly_sales?: number | null;
  amazon_seller_count?: number | null;
  amazon_offered_by_amazon?: boolean | null;
  algopix_recommendation?: string | null;
  ebay_price?: number | null;
  // W6.1 — SP-API risk flags (display-only; NULL=unchecked post-W5a; ZERO engine reads). Via select('*').
  is_gated?: boolean | null;
  is_hazmat?: boolean | null;
  // W5b — eBay sold-velocity (display-only; Correction-1 column, NOT engine-read). Arrives via select('*').
  ebay_sold_units_monthly?: number | null;
  ebay_sold_n_transactions?: number | null;
  ebay_sold_n_units?: number | null;
  ebay_sold_median_unit_price?: number | null;
  ebay_sold_median_landed_price?: number | null;
  ebay_sold_price_mix?: {
    auction?: number;
    bin_exact?: number;
    bin_ceiling?: number;
    qty_defaulted?: number;
    price_included?: number;
  } | null;
  ebay_sold_window_days?: number | null;
  ebay_velocity_fetched_at?: string | null;
  walmart_price?: number | null;
  algopix_data?: Record<string, unknown> | null;
  source_row_number?: number | null;
  import_id?: string | null;
  // K5.3 (F6) — clearance master/lot marker + self-FK. Arrive via select('*') at runtime; typed here
  // (the interface has a [key: string]: unknown index sig, so this is additive) for the lot chip,
  // composite-warning + lot-vs-master branching. No new column.
  is_clearance_lot?: boolean | null;
  parent_product_id?: string | null;
  ingestion_batches?: {
    id?: string;
    file_name?: string | null;
    created_at?: string | null;
    upstream_source_text?: string | null;
  } | null;
  assigned_buyer_user_id?: string | null;
  // Day 83 P6.2 — per-field provenance JSONB sidecar (Day 82 L1 envelope shape).
  // Arrives via `select('*')` at useCatalog.ts; consumed by ProductDetail KV
  // wraps for cost_usd + algopix_data Section 7.
  provenance?: ProvenanceMap | null;
  // Day 90 (CF 60) — per-channel permission policy at product tier per ADR 46.
  // Shape: Record<marketplaces.channel, boolean> | null. Consumed by ProductDetail
  // Marketplace Permissions section (admin-gated picker).
  marketplace_permissions?: Record<string, boolean> | null;
  [key: string]: unknown;
}

export interface ProductDetailProfitability {
  /** pp.marketplace — enum-typed stored column; values are fpc path keys
   *  (the stored name renames only when the enum dies, slice-B family). */
  marketplace: string;
  profit_per_unit?: number | null;
  roi_pct?: number | null;
  profit_per_day_safety?: number | null;
  roi_per_day_safety?: number | null;
  effective_floor?: number | null;
  sale_price?: number | null;
  total_cost_per_unit?: number | null;
  marketplace_fees?: number | null;
  referral_fee?: number | null;
  fba_fee?: number | null;
  returns_reserve?: number | null;
  no_price_data?: boolean | null;
  is_profitable?: boolean | null;
  meets_min_roi?: boolean | null;
  monthly_sales?: number | null;
  // Day 84 P6.3 L7 — per-row source-class flags from engine cache.
  // 'manual_override' | 'per_path' for sale_price; 'manual_override' | 'algopix' for velocity.
  sale_price_source?: string | null;
  velocity_source?: string | null;
  // W4c — the Amazon fee COALESCE tier that won (spapi | keepa | flat). Rendered on the Cost
  // Breakdown via sourcePrefix(); NULL until the engine recomputes (caption hidden when null).
  amazon_fee_source?: string | null;
}

/** Day 84 P6.3 — velocity_overrides row shape. Per (product_id, marketplace)
 *  unique (marketplace = the enum-typed stored column; values are fpc path
 *  keys — the name renames only at the enum's death, slice-B family). Engine
 *  reads via COALESCE in calculate_profitability + _tiers (override → algopix
 *  per L6 2-tier). UI consumes via per-path lookup in ProductDetail Section 6
 *  for italic-source rendering. */
export interface VelocityOverrideRow {
  id: string;
  product_id: string;
  marketplace: string;
  monthly_sales: number;
  notes: string | null;
  set_by: string | null;
  provenance: ProvenanceMap | null;
  created_at: string;
  updated_at: string;
}

/** Day 84 P6.3 — price_overrides row shape. Per (product_id, marketplace)
 *  unique (the same enum-typed stored `marketplace` column as
 *  velocity_overrides; path-key values). Engine reads via COALESCE in
 *  calculate_profitability + _tiers + _breakdown_extras (override → C.4b
 *  per-path chain). */
export interface PriceOverrideRow {
  id: string;
  product_id: string;
  marketplace: string;
  sale_price: number;
  notes: string | null;
  set_by: string | null;
  provenance: ProvenanceMap | null;
  created_at: string;
  updated_at: string;
}

interface ProductDetailFlag {
  id: string;
  status: string;
  flag_category: string | null;
  flag_reason: string | null;
  created_at: string;
  resolved_by?: string | null;
  resolved_at?: string | null;
}

interface ProductDetailImportInfo {
  id?: string;
  file_name?: string | null;
  created_at?: string | null;
  // K5.3 (F7) — clearance upstream provenance (ingestion_batches.upstream_source_text).
  upstream_source_text?: string | null;
}

interface ProductDetailListingLink {
  id: string;
  /** listing_links.marketplace — a TEXT column of channel display names
   *  ('Amazon'/'eBay'/…): a stored name OUTSIDE the enum family (does not
   *  rename at the enum's death). */
  marketplace: string;
  url: string;
  status?: string | null;
  listing_id?: string | null;
  notes?: string | null;
}

/** U2 S6 — a variant of this product (the identity-core replacement for the removed
 *  lots-as-products model). Rows render isg_sku + condition; the per-variant listing-link
 *  action keys off `id`. */
interface ProductDetailVariant {
  id: string;
  isg_sku: string;
  condition: string;
  created_at: string;
}

export interface UseProductDetailResult {
  product: ProductDetailRow | null;
  profitability: ProductDetailProfitability[];
  flags: ProductDetailFlag[];
  loading: boolean;
  importInfo: ProductDetailImportInfo | null;
  importProductCount: number | null;
  listingLinks: ProductDetailListingLink[];
  // Day 84 P6.3 L11 — override arrays fetched parallel with profitability;
  // consumed by Section 6 per-path lookup for italic-source rendering.
  velocityOverrides: VelocityOverrideRow[];
  priceOverrides: PriceOverrideRow[];
  resolveFlag: (
    flagId: string,
    resolvedBy: string | null
  ) => Promise<{ error: { message: string } | null }>;
  dismissFlag: (flagId: string) => Promise<{ error: { message: string } | null }>;
  refreshLinks: () => Promise<void>;
  refreshFlags: () => Promise<void>;
  refreshOverrides: () => Promise<void>;
  /** Day 98.5b — re-run the record fetch (concurrent-edit banner Refresh). */
  refetch: () => void;
  // U2 S6 — the product's variants (identity core; replaced the clearance master/lot relations).
  // Drives the Variants panel; refreshVariants re-reads after a create-variant / listing-link write.
  variants: ProductDetailVariant[];
  refreshVariants: () => Promise<void>;
}

// ── Product detail loader ───────────────────────────────────────────────────

export function useProductDetail(isgSku: string | null | undefined): UseProductDetailResult {
  const [product, setProduct] = useState<ProductDetailRow | null>(null);
  const [profitability, setProfitability] = useState<ProductDetailProfitability[]>([]);
  const [flags, setFlags] = useState<ProductDetailFlag[]>([]);
  const [importInfo, setImportInfo] = useState<ProductDetailImportInfo | null>(null);
  const [importProductCount, setImportProductCount] = useState<number | null>(null);
  const [listingLinks, setListingLinks] = useState<ProductDetailListingLink[]>([]);
  // Day 84 P6.3 L11 — override arrays parallel to profitability/flags/links.
  const [velocityOverrides, setVelocityOverrides] = useState<VelocityOverrideRow[]>([]);
  const [priceOverrides, setPriceOverrides] = useState<PriceOverrideRow[]>([]);
  // U2 S6 — the product's variants (identity core; replaced the clearance master/lot relations).
  const [variants, setVariants] = useState<ProductDetailVariant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // Day 98.5b — bump to re-run the fetch effect (concurrent-edit banner Refresh).
  const [reloadKey, setReloadKey] = useState<number>(0);

  useEffect(() => {
    if (!isgSku) return;
    setLoading(true);
    setVariants([]);

    Promise.all([
      supabase
        .from('products')
        // U10 A1 cost migration — the junction embed (primary product_distributors
        // row) is the canonical cost read; products.cost_usd (rides the star) is the
        // trigger-maintained read-copy that DROPS at slice B. The embedded filter
        // narrows the embed only (no !inner — a no-primary product still returns).
        .select(
          '*, ingestion_batches(id, file_name, created_at, upstream_source_text), product_distributors ( cost_usd, is_primary )'
        )
        .eq('product_distributors.is_primary', true)
        .eq('isg_sku', isgSku)
        .single(),
    ])
      .then(async ([productRes]) => {
        const prod = (productRes.data as unknown as ProductDetailRow | null) ?? null;
        // Re-ground cost on the junction before hand-off: no-primary → NULL → '—'
        // (the read-copy's retain-last-known edge dies — the ruled post-drop semantic).
        if (prod) {
          prod.cost_usd = prod.product_distributors?.[0]?.cost_usd ?? null;
        }
        setProduct(prod);

        // Extract import info from the join
        if (prod?.ingestion_batches) {
          setImportInfo(prod.ingestion_batches);
        }

        if (prod?.id) {
          const [profRes, flagRes, linksRes, vOvrRes, pOvrRes, variantsRes] = await Promise.all([
            supabase.from('product_profitability').select('*').eq('product_id', prod.id),
            supabase
              .from('product_flags')
              .select('*')
              .eq('product_id', prod.id)
              .order('created_at', { ascending: false }),
            callRpc('get_listing_links', { p_product_id: prod.id }),
            // Day 84 P6.3 L11 — fetch override rows in parallel
            supabase.from('velocity_overrides').select('*').eq('product_id', prod.id),
            // U2 S4 (Approach F): price_overrides re-keyed product_id→variant_id; read via the
            // variant embed so post-freeze rows (product_id NULL) are found. velocity_overrides
            // stays product-keyed (out of U2 scope).
            supabase
              .from('price_overrides')
              .select('*, variants!inner(product_id)')
              .eq('variants.product_id', prod.id),
            // U2 S6 — the product's variants (identity core). Ordered by creation so the first
            // minted variant leads; condition + isg_sku render in the Variants panel.
            supabase
              .from('variants')
              .select('id, isg_sku, condition, created_at')
              .eq('product_id', prod.id)
              .order('created_at', { ascending: true }),
          ]);
          setProfitability((profRes.data as unknown as ProductDetailProfitability[]) || []);
          setFlags((flagRes.data as unknown as ProductDetailFlag[]) || []);
          setListingLinks((linksRes.data as ProductDetailListingLink[]) || []);
          setVelocityOverrides((vOvrRes.data as unknown as VelocityOverrideRow[]) || []);
          setPriceOverrides((pOvrRes.data as unknown as PriceOverrideRow[]) || []);
          setVariants((variantsRes.data as unknown as ProductDetailVariant[]) || []);

          // Live product count for import — MASTERS-only (K5.3-tail, U7 S3): exclude child
          // clearance lots (parent_product_id NOT NULL), legacy in the new variant×event grain.
          if (prod.import_id) {
            supabase
              .from('products')
              .select('id', { count: 'exact', head: true })
              .eq('import_id', prod.import_id)
              .is('parent_product_id', null)
              .then(({ count }) => setImportProductCount(count));
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isgSku, reloadKey]);

  const resolveFlag = useCallback(
    async (
      flagId: string,
      resolvedBy: string | null
    ): Promise<{ error: { message: string } | null }> => {
      const { error } = await supabase
        .from('product_flags')
        .update({
          status: 'resolved',
          resolved_by: resolvedBy,
          resolved_at: new Date().toISOString(),
        } as never)
        .eq('id', flagId);
      if (!error)
        setFlags((prev) => prev.map((f) => (f.id === flagId ? { ...f, status: 'resolved' } : f)));
      return { error };
    },
    []
  );

  const dismissFlag = useCallback(
    async (flagId: string): Promise<{ error: { message: string } | null }> => {
      const { error } = await supabase
        .from('product_flags')
        .update({ status: 'dismissed' } as never)
        .eq('id', flagId);
      if (!error)
        setFlags((prev) => prev.map((f) => (f.id === flagId ? { ...f, status: 'dismissed' } : f)));
      return { error };
    },
    []
  );

  const refreshLinks = useCallback(async (): Promise<void> => {
    if (!product?.id) return;
    const { data } = await callRpc('get_listing_links', { p_product_id: product.id });
    setListingLinks((data as ProductDetailListingLink[]) || []);
  }, [product?.id]);

  // U2 S6 — re-read variants after a create-variant / per-variant listing-link write.
  const refreshVariants = useCallback(async (): Promise<void> => {
    if (!product?.id) return;
    const { data } = await supabase
      .from('variants')
      .select('id, isg_sku, condition, created_at')
      .eq('product_id', product.id)
      .order('created_at', { ascending: true });
    setVariants((data as unknown as ProductDetailVariant[]) || []);
  }, [product?.id]);

  const refreshFlags = useCallback(async (): Promise<void> => {
    if (!product?.id) return;
    const { data } = await supabase
      .from('product_flags')
      .select('*')
      .eq('product_id', product.id)
      .order('created_at', { ascending: false });
    setFlags((data as unknown as ProductDetailFlag[]) || []);
  }, [product?.id]);

  // Day 84 P6.3 L11 — fired after OverrideEditModal save/remove. Refetches both
  // override arrays + the profitability cache (cache stale until next engine
  // recalc; UI reads override rows directly for immediate post-save accuracy
  // via per-path lookup in ProductDetail Section 6).
  const refreshOverrides = useCallback(async (): Promise<void> => {
    if (!product?.id) return;
    const [vRes, pRes, profRes] = await Promise.all([
      supabase.from('velocity_overrides').select('*').eq('product_id', product.id),
      supabase.from('price_overrides').select('*').eq('product_id', product.id),
      supabase.from('product_profitability').select('*').eq('product_id', product.id),
    ]);
    setVelocityOverrides((vRes.data as unknown as VelocityOverrideRow[]) || []);
    setPriceOverrides((pRes.data as unknown as PriceOverrideRow[]) || []);
    setProfitability((profRes.data as unknown as ProductDetailProfitability[]) || []);
  }, [product?.id]);

  const refetch = useCallback((): void => setReloadKey((k) => k + 1), []);

  return {
    product,
    profitability,
    flags,
    loading,
    importInfo,
    importProductCount,
    listingLinks,
    velocityOverrides,
    priceOverrides,
    resolveFlag,
    dismissFlag,
    refreshLinks,
    refreshFlags,
    refreshOverrides,
    refetch,
    variants,
    refreshVariants,
  };
}
